import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  ICloseDecree,
  IConfirmDecree,
  ICreateDecree,
  IDecreeBoxResponse,
  IDecreeListResponse,
  IReturnParcel,
} from "dto/Decree/IDecree";

export type DecreeBox = "received" | "issued";

const queryKeys = {
  parcelDecrees: (parcelId: string) => ["decreeService.parcelDecrees", parcelId],
  box: (box: DecreeBox, includeClosed: boolean) => [
    "decreeService.box",
    box,
    includeClosed,
  ],
};

const getParcelDecrees = (
  parcelId: string,
): Promise<{ data: IDecreeListResponse }> => {
  return axiosInstance.get(`incomingParcel/${parcelId}/decrees`);
};

const useGetParcelDecrees = (parcelId: string) => {
  return useCustomQuery(
    queryKeys.parcelDecrees(parcelId),
    () => getParcelDecrees(parcelId),
    { enabled: !!parcelId },
  );
};

const addDecree = (payload: ICreateDecree): Promise<{ data: unknown }> => {
  return axiosInstance.post("incomingParcel/decree", payload);
};

const useAddDecree = () => {
  return useCustomMutation(addDecree);
};

const closeDecree = (payload: ICloseDecree): Promise<{ data: unknown }> => {
  return axiosInstance.post("incomingParcel/decree/close", payload);
};

const useCloseDecree = () => {
  return useCustomMutation(closeDecree);
};

const deleteDecree = (decreeId: string): Promise<{ data: unknown }> => {
  return axiosInstance.delete(`incomingParcel/decree/${decreeId}`);
};

const useDeleteDecree = () => {
  return useCustomMutation(deleteDecree);
};

/** Zwrot przesyłki z powodem — do autora dekretu albo biura podawczego. */
const returnParcel = (payload: IReturnParcel): Promise<{ data: unknown }> => {
  return axiosInstance.post("incomingParcel/decree/return", payload);
};

const useReturnParcel = () => {
  return useCustomMutation(returnParcel);
};

/** Potwierdzenie zapoznania z dekretem „do wiadomości za potwierdzeniem". */
const confirmDecree = (payload: IConfirmDecree): Promise<{ data: unknown }> => {
  return axiosInstance.post("incomingParcel/decree/confirm", payload);
};

const useConfirmDecree = () => {
  return useCustomMutation(confirmDecree);
};

const getBox = (
  box: DecreeBox,
  includeClosed: boolean,
): Promise<{ data: IDecreeBoxResponse }> => {
  return axiosInstance.get("decrees/box", {
    params: { box, includeClosed },
  });
};

const useGetBox = (box: DecreeBox, includeClosed: boolean) => {
  return useCustomQuery(
    queryKeys.box(box, includeClosed),
    () => getBox(box, includeClosed),
    { refetchInterval: 60_000 },
  );
};

export default {
  queryKeys,
  useGetParcelDecrees,
  useAddDecree,
  useCloseDecree,
  useDeleteDecree,
  useReturnParcel,
  useConfirmDecree,
  useGetBox,
};
